"use client";

import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Send, CheckCircle, Instagram } from "lucide-react";
import { toast } from "sonner";
import {
  trackContactFormSubmitted,
  trackDealerContactSubmitted,
} from "@/lib/posthog-client";
import { JsonLdScript } from "@/components/seo/json-ld-script";

export default function ContactPage() {
  const [isDealerIntent, setIsDealerIntent] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    intent: "general",
    shopName: "",
    website: "",
    monthlyVolume: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    const dealerIntent = new URLSearchParams(window.location.search).get("intent") === "dealer";
    setIsDealerIntent(dealerIntent);

    if (!dealerIntent) return;

    setFormData((prev) => ({
      ...prev,
      intent: "dealer",
      subject: prev.subject || "Dealer volume pricing",
      message:
        prev.message ||
        "I'd like to try The Wheel Visualizer with customers in our shop.",
    }));
  }, [isDealerIntent]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setIsSuccess(false);

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to send message");
      }

      setIsSuccess(true);
      setFormData({
        name: "",
        email: "",
        subject: isDealerIntent ? "Dealer volume pricing" : "",
        message: isDealerIntent
          ? "I'd like to try The Wheel Visualizer with customers in our shop."
          : "",
        intent: isDealerIntent ? "dealer" : "general",
        shopName: "",
        website: "",
        monthlyVolume: "",
      });
      toast.success("Message sent successfully! We'll get back to you soon.");

      // Track contact form submission in PostHog
      trackContactFormSubmitted({
        subject: formData.subject,
        intent: formData.intent === "dealer" ? "dealer" : "general",
      });
      if (formData.intent === "dealer") {
        trackDealerContactSubmitted({
          subject: formData.subject,
          shopName: formData.shopName || undefined,
        });
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error(error instanceof Error ? error.message : "Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <div className="container py-8 max-w-2xl">
      <JsonLdScript
        data={{
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: "Contact Us - The Wheel Visualizer",
            url: "https://thewheelvisualizer.com/contact",
            description: "Have a question or feedback? We'd love to hear from you. Send us a message and we'll respond as soon as possible.",
            mainEntity: {
              "@type": "Organization",
              name: "The Wheel Visualizer",
              url: "https://thewheelvisualizer.com",
              contactPoint: {
                "@type": "ContactPoint",
                email: "support@thewheelvisualizer.com",
                contactType: "Customer Service",
              },
            },
        }}
      />
      <Card>
        <CardHeader>
          <CardTitle className="text-3xl">
            {isDealerIntent ? "Contact Sales" : "Contact Us"}
          </CardTitle>
          <CardDescription>
            {isDealerIntent
              ? "Tell us about your shop and we'll help you test the visualizer with real customers."
              : "Have a question or feedback? We'd love to hear from you. Send us a message and we'll respond as soon as possible."}
          </CardDescription>
          <div className="mt-4 flex items-center gap-2">
            <a
              href="https://instagram.com/wheelvisualizer"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <Instagram className="w-4 h-4" />
              <span>@wheelvisualizer</span>
            </a>
          </div>
        </CardHeader>
        <CardContent>
          {isSuccess ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Message Sent!</h3>
              <p className="text-muted-foreground mb-6">
                Thank you for contacting us. We&apos;ll get back to you soon.
              </p>
              <Button
                onClick={() => {
                  setIsSuccess(false);
                  setFormData({
                    name: "",
                    email: "",
                    subject: isDealerIntent ? "Dealer volume pricing" : "",
                    message: isDealerIntent
                      ? "I'd like to try The Wheel Visualizer with customers in our shop."
                      : "",
                    intent: isDealerIntent ? "dealer" : "general",
                    shopName: "",
                    website: "",
                    monthlyVolume: "",
                  });
                }}
              >
                Send Another Message
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  disabled={isSubmitting}
                />
              </div>

              {isDealerIntent && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="shopName">Shop name</Label>
                    <Input
                      id="shopName"
                      name="shopName"
                      type="text"
                      value={formData.shopName}
                      onChange={handleChange}
                      placeholder="Your wheel or tire shop"
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="website">Website or Instagram</Label>
                    <Input
                      id="website"
                      name="website"
                      type="text"
                      value={formData.website}
                      onChange={handleChange}
                      placeholder="https://..."
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="monthlyVolume">Monthly wheel volume</Label>
                    <Input
                      id="monthlyVolume"
                      name="monthlyVolume"
                      type="text"
                      value={formData.monthlyVolume}
                      onChange={handleChange}
                      placeholder="Example: 20-50 customers/month"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                  disabled={isSubmitting}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  name="subject"
                  type="text"
                  required
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="What's this about?"
                  disabled={isSubmitting}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Message</Label>
                <textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us more about your question or feedback..."
                  disabled={isSubmitting}
                  rows={6}
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                />
              </div>

              <Button type="submit" disabled={isSubmitting} className="w-full">
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
